import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationDialogComponent } from '../../../controls/fnf-confirmation/confirmation-dialog/confirmation-dialog.component';
import { RestaurantService } from '../../../../services/restaurant.service';
import { FNFResponse } from '../../../../models/response';
import { isPosManaged } from '../../../../utils/pos-provider.util';

@Component({
  selector: 'app-add-type',
  standalone: true,
  imports: [ReactiveFormsModule, MatIcon, CommonModule, FormsModule],
  templateUrl: './add-type.component.html',
  styleUrl: './add-type.component.css',
})
export class AddTypeComponent {
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private spinner = inject(NgxSpinnerService);
  restaurantService = inject(RestaurantService);

  /** Set on the edit route; null when adding. */
  type: any = null;
  allTypes: any[] = [];
  loading = true;
  isExiting = false;
  restaurantPosProvider = 'fnf';

  typeForm: FormGroup = this.buildForm(null);

  /** Index of the category row currently open for rename, or null. */
  editingIndex: number | null = null;
  /** Pre-edit name, so Cancel can put the row back. */
  private editingOriginal = '';
  editingError = '';
  /** The new-category panel is only shown once the user asks for it. */
  addingCategory = false;
  newCategoryName = '';
  newCategoryError = '';

  constructor() {
    this.restaurantService.restaurantPosProvider$.subscribe((posProvider: any) => {
      this.restaurantPosProvider = posProvider;
    });
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => this.load(params.get('id')));
  }

  private load(typeId: string | null) {
    this.loading = true;
    this.restaurantService.getRestaurantTypeAndCategories().subscribe({
      next: (response: FNFResponse<any>) => {
        this.allTypes = response?.data || [];
        this.type = typeId
          ? this.allTypes.find((t: any) => String(t.id) === String(typeId)) || null
          : null;
        this.typeForm = this.buildForm(this.type);
        if (this.isPosManagedEdit) {
          this.typeForm.disable();
        }
        this.loading = false;
      },
      error: () => {
        this.toastr.error('Unable to load the type. Please try again later.', 'Error');
        this.loading = false;
        this.router.navigate(['/main/type-and-categories']);
      },
    });
  }

  private buildForm(type: any): FormGroup {
    return this.fb.group({
      typeName: [
        type?.name ?? '',
        [Validators.required, Validators.minLength(3), Validators.maxLength(50)],
      ],
      description: [type?.description ?? '', [Validators.maxLength(200)]],
      isMandatory: [type?.is_mandatory ?? false],
      isMultiSelect: [type?.is_multi_category ?? false],
      isDefault: [type?.is_default ?? false],
      categories: this.fb.array(
        (type?.categories ?? []).map((c: any) =>
          this.fb.group({
            id: [c.id],
            name: [
              c.name,
              [Validators.required, Validators.minLength(3), Validators.maxLength(50)],
            ],
          }),
        ),
        [this.duplicateValidator],
      ),
    });
  }

  get categories(): FormArray {
    return this.typeForm.get('categories') as FormArray;
  }

  get isPosManaged(): boolean {
    return isPosManaged(this.restaurantPosProvider);
  }

  /** POS-managed types sync from Clover/Square: content is read-only once opened for edit. */
  get isPosManagedEdit(): boolean {
    return this.isPosManaged && !!this.type;
  }

  duplicateValidator(control: AbstractControl): ValidationErrors | null {
    const formArray = control as FormArray;
    const values = formArray.controls
      .map((c) => c.value?.name?.trim().toLowerCase())
      .filter((value: string) => value !== '');
    const hasDuplicates = values.some((value, index) => values.indexOf(value) !== index);
    return hasDuplicates ? { duplicate: true } : null;
  }

  setSelection(isMulti: boolean) {
    this.typeForm.get('isMultiSelect')?.setValue(isMulti);
  }

  // ------------------------------------------------------------ categories

  startAddCategory() {
    this.addingCategory = true;
    this.newCategoryName = '';
    this.newCategoryError = '';
  }

  cancelAddCategory() {
    this.addingCategory = false;
    this.newCategoryName = '';
    this.newCategoryError = '';
  }

  confirmAddCategory() {
    const name = this.newCategoryName.trim();
    if (!name) {
      this.newCategoryError = 'Category name cannot be empty.';
      return;
    }
    if (name.length < 3 || name.length > 50) {
      this.newCategoryError = 'Category name must be between 3 and 50 characters.';
      return;
    }
    if (
      this.categories.value.some((c: any) => c.name?.trim().toLowerCase() === name.toLowerCase())
    ) {
      this.newCategoryError = 'That category already exists under this type.';
      return;
    }

    this.categories.push(
      this.fb.group({
        id: [''],
        name: [name, [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
      }),
    );
    this.typeForm.updateValueAndValidity();
    this.cancelAddCategory();
  }

  startEditCategory(index: number) {
    this.editingIndex = index;
    this.editingOriginal = this.categories.at(index).get('name')?.value ?? '';
    this.editingError = '';
  }

  cancelEditCategory() {
    if (this.editingIndex === null) {
      return;
    }
    // Put back what was there before the row was opened.
    this.categories.at(this.editingIndex).get('name')?.setValue(this.editingOriginal);
    this.editingIndex = null;
    this.editingError = '';
    this.typeForm.updateValueAndValidity();
  }

  saveEditCategory() {
    if (this.editingIndex === null) {
      return;
    }
    const index = this.editingIndex;
    const name = (this.categories.at(index).get('name')?.value ?? '').trim();

    if (!name) {
      this.editingError = 'Category name cannot be empty.';
      return;
    }
    if (name.length < 3 || name.length > 50) {
      this.editingError = 'Category name must be between 3 and 50 characters.';
      return;
    }
    const clashes = this.categories.value.some(
      (c: any, i: number) => i !== index && c.name?.trim().toLowerCase() === name.toLowerCase(),
    );
    if (clashes) {
      this.editingError = 'That category already exists under this type.';
      return;
    }

    this.categories.at(index).get('name')?.setValue(name);
    this.editingIndex = null;
    this.editingError = '';
    this.typeForm.updateValueAndValidity();
  }

  removeCategory(index: number) {
    this.categories.removeAt(index);
    if (this.editingIndex === index) {
      this.editingIndex = null;
      this.editingError = '';
    } else if (this.editingIndex !== null && this.editingIndex > index) {
      // Rows after the removed one shift down, so the open row follows.
      this.editingIndex -= 1;
    }
    this.typeForm.updateValueAndValidity();
  }

  // ---------------------------------------------------------------- submit

  filterType() {
    const name = (this.typeForm.get('typeName')?.value || '').trim().toLowerCase();
    this.isExiting = this.allTypes.some(
      (f: any) => f.name.trim().toLowerCase() === name && f.name !== this.type?.name,
    );
  }

  onDefaultChange(checked: boolean) {
    const existingDefault = this.allTypes.find((f: any) => f.is_default);
    if (checked && existingDefault && existingDefault.id !== this.type?.id) {
      this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message: 'This will be update the exiting default type. Do you want to continue?',
          buttonText: { ok: 'Yes, Confirm' },
        },
      });
    }
  }

  /**
   * Reached from Add Menu as well as from Types & Categories; returnTo carries
   * the caller so the menu being written is not left behind.
   */
  private get returnUrl(): string {
    return this.route.snapshot.queryParamMap.get('returnTo') || '/main/type-and-categories';
  }

  cancel() {
    this.router.navigateByUrl(this.returnUrl);
  }

  save() {
    if (this.typeForm.invalid || this.isExiting) {
      return;
    }

    const payload: any = {
      name: this.typeForm.get('typeName')?.value,
      categories: this.categories.value,
      is_multi_category: this.typeForm.get('isMultiSelect')?.value,
      is_mandatory: this.typeForm.get('isMandatory')?.value,
      is_default: this.typeForm.get('isDefault')?.value,
    };
    if (this.type) {
      payload.id = this.type.id;
    }
    // Types have never carried a description server-side, so only send one
    // when the user actually wrote something; an untouched form posts exactly
    // what it always did.
    const description = (this.typeForm.get('description')?.value || '').trim();
    if (description) {
      payload.description = description;
    }

    this.spinner.show();
    const request = this.type
      ? this.restaurantService.editMenuType(payload)
      : this.restaurantService.addRestaurantType(payload);

    request.subscribe({
      next: (response: FNFResponse<any>) => {
        this.spinner.hide();
        if (!response.data) {
          const message = response.error?.error?.message || response.error?.message;
          this.toastr.error(message || 'No data found!', 'Error');
          return;
        }
        this.toastr.success(response.data.message || 'Saved successfully', 'Success');
        this.router.navigateByUrl(this.returnUrl);
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }
}
